<script setup>
import { computed, ref, watch } from 'vue'

import CastIronRadiator from './CastIronRadiator.vue'
import DeVariantRadiator from './DeVariantRadiator.vue'
import {
  existingRadiatorData,
  radiatorWattsAtTemp,
  getTemperatureConditions,
  TEMPERATURE_CONDITIONS,
} from '../lib/existing-radiator'
import { trackEvent } from '../lib/tracking'

const DE_MARKETS = ['GermanyPurmo'] //, 'AustriaVN']
const PL_MARKETS = ['PolandPurmo']

const props = defineProps({
  strings: {
    type: Object,
    default: null,
  },
  market: {
    type: String,
    default: '',
  },
})

const isDeMarket = computed(() => DE_MARKETS.includes(props.market))
const isPlMarket = computed(() => PL_MARKETS.includes(props.market))

const typeValue = ref('')
const heightValue = ref('')
const lengthValue = ref('')
const temperatureCondition = ref('55/45/20')

const temperatureOptions = getTemperatureConditions()

const copy = computed(() => props.strings?.existingRadiator ?? {})

const selectedTemps = computed(() => TEMPERATURE_CONDITIONS[temperatureCondition.value])

const watts = computed(() => {
  if (!typeValue.value || !heightValue.value || !lengthValue.value) return 0
  try {
    return radiatorWattsAtTemp(
      Number(typeValue.value),
      Number(heightValue.value),
      Number(lengthValue.value),
      selectedTemps.value
    )
  } catch (error) {
    return 0
  }
})

const resultLabel = computed(() => {
  const template =
    copy.value.resultLabelTemplate ||
    'Heat output requirement, calculated at {temp} system temperature'
  return template.replace('{temp}', temperatureCondition.value)
})

watch(watts, (val) => {
  if (val > 0) {
    trackEvent('calculator_existing_result', {
      type: typeValue.value,
      height: heightValue.value,
      length: lengthValue.value,
      temperature: temperatureCondition.value,
      watts: val,
      market: props.market,
    })
  }
})
</script>

<template>
  <section v-if="!isDeMarket && !isPlMarket" class="existing-radiator">
    <p class="existing-radiator__description">
      {{
        copy.description ||
        'This calculator is for information only. Always consult a professional installer for the exact calculation before purchasing the radiator.'
      }}
    </p>
    <div class="existing-radiator__fields">
      <label class="existing-radiator__field">
        <span class="existing-radiator__label">{{ copy.type || 'Radiator Type' }}</span>
        <span class="existing-radiator__select">
          <select v-model="typeValue">
            <option value="" disabled>{{ copy.select || 'Select' }}</option>
            <option v-for="type in existingRadiatorData.types" :key="type" :value="type">
              {{ type }}
            </option>
          </select>
        </span>
      </label>
      <label class="existing-radiator__field">
        <span class="existing-radiator__label">{{ copy.height || 'Radiator Height' }}</span>
        <span class="existing-radiator__select">
          <select v-model="heightValue">
            <option value="" disabled>{{ copy.select || 'Select' }}</option>
            <option v-for="height in existingRadiatorData.heights" :key="height" :value="height">
              {{ height }} mm
            </option>
          </select>
        </span>
      </label>
      <label class="existing-radiator__field">
        <span class="existing-radiator__label">{{ copy.length || 'Radiator Length' }}</span>
        <span class="existing-radiator__select">
          <select v-model="lengthValue">
            <option value="" disabled>{{ copy.select || 'Select' }}</option>
            <option v-for="length in existingRadiatorData.lengths" :key="length" :value="length">
              {{ length }} mm
            </option>
          </select>
        </span>
      </label>
      <!-- Temperature selector hidden for now - feature not yet released -->
      <label class="existing-radiator__field">
        <span class="existing-radiator__label">{{ copy.temperature || 'System Temperature' }}</span>
        <span class="existing-radiator__select">
          <select v-model="temperatureCondition">
            <option v-for="opt in temperatureOptions" :key="opt.key" :value="opt.key">
              {{ opt.label }}
            </option>
          </select>
        </span>
      </label>
    </div>

    <p class="existing-radiator__result-label">
      {{ resultLabel }}
    </p>
    <div class="existing-radiator__result">
      <span class="existing-radiator__value">{{ watts }}</span>
      <span class="existing-radiator__unit">W</span>
    </div>
  </section>
  <DeVariantRadiator v-else-if="isDeMarket" :strings="strings" :market="props.market" />
  <CastIronRadiator v-else-if="isPlMarket" :strings="strings" :market="props.market" />
</template>

<style scoped>
.existing-radiator {
  display: grid;
  gap: 16px;
  padding: 16px 0;
  border-top: 1px solid #e4eaed;
}

.existing-radiator__description {
  margin: 0;
  font-size: 0.85rem;
  color: #6b7b7f;
}

.existing-radiator__fields {
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr;
}

.existing-radiator__field {
  display: grid;
  gap: 6px;
  font-size: 0.85rem;
  color: #4e6b6e;
}

.existing-radiator__label {
  font-weight: 600;
}

.existing-radiator__select select {
  width: 100%;
  height: 36px;
  padding: 8px 40px 8px 10px;
  border: 1px solid var(--semantic-border-muted);
  border-radius: 2px;
  background-color: var(--semantic-bg-default);
  color: var(--semantic-text-default);
  font-size: 0.85rem;
  appearance: none;
  outline: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath d='M7 10l5 5 5-5' fill='none' stroke='%23888' stroke-width='2'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 18px 18px;
}

.existing-radiator__result {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  border: 1px solid var(--semantic-border-muted);
  border-radius: 2px;
  background: var(--semantic-bg-default);
  font-weight: 700;
  color: #0f2b2f;
  width: fit-content;
  margin: 8px auto 0;
}

.existing-radiator__result-label {
  margin: var(--space-64) 0 0;
  font-size: 0.8rem;
  font-weight: 500;
  color: #6b7b7f;
  text-align: center;
}

.existing-radiator__value {
  font-size: 1.2rem;
  outline: none;
}

.existing-radiator__unit {
  font-size: 0.8rem;
  color: #607276;
}
</style>
